import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth";
import Logo from "@/components/Logo";

export default function ConfirmEmail() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user, refresh } = useAuth();
  const [state, setState] = useState("loading"); // loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!token) {
      setState("error");
      setErrorMsg(t("confirm_email_missing_token"));
      return;
    }
    api
      .post(`/auth/confirm-email/${encodeURIComponent(token)}`)
      .then(async () => {
        setState("success");
        // If the user already had a session in this browser, refresh the
        // auth state so the "email_verified=true" flag propagates.
        try { await refresh?.(); } catch (_) { /* noop */ }
      })
      .catch((e) => {
        setState("error");
        setErrorMsg(e?.response?.data?.detail || t("confirm_email_error_default"));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // After success, route the user to where they actually have access:
  //  - logged in on free plan:  /plans?welcome=1
  //  - logged in already paid: /dashboard
  //  - not logged: /login?confirmed=1  (we show a green banner there; Login.jsx
  //    reads sessionStorage('ks_post_confirm') to walk free-plan users through
  //    /plans?welcome=1 after they sign in.)
  const onContinue = () => {
    try { sessionStorage.setItem("ks_post_confirm", "1"); } catch (_) { /* noop */ }
    if (user) {
      const plan = user.plan;
      if (!plan || plan === "free") navigate("/plans?welcome=1");
      else navigate("/dashboard");
    } else {
      navigate("/login?confirmed=1");
    }
  };

  return (
    <div
      className="min-h-screen-safe bg-obsidian text-white flex items-center justify-center px-6"
      data-testid="confirm-email-page"
    >
      <div className="glass-card rounded-md p-10 max-w-md w-full text-center">
        <div className="mb-6 flex justify-center"><Logo /></div>

        {state === "loading" && (
          <>
            <Loader2 className="w-10 h-10 text-gold animate-spin mx-auto mb-4" />
            <h1 className="font-display text-2xl text-gold-light mb-2">{t("confirm_email_loading_title")}</h1>
            <p className="text-muted text-sm">{t("confirm_email_loading_body")}</p>
          </>
        )}

        {state === "success" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h1 className="font-display text-2xl text-gold-light mb-2">{t("confirm_email_success_title")}</h1>
            <p className="text-muted text-sm mb-6">{t("confirm_email_success_body")}</p>
            <button
              onClick={onContinue}
              className="btn-gold w-full justify-center"
              data-testid="confirm-go-dashboard"
            >
              {user ? t("confirm_email_go_dashboard") : t("confirm_email_go_login")}
            </button>
          </>
        )}

        {state === "error" && (
          <>
            <AlertTriangle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
            <h1 className="font-display text-2xl text-gold-light mb-2">{t("confirm_email_error_title")}</h1>
            <p className="text-muted text-sm mb-6">{errorMsg}</p>
            <p className="text-faded text-xs mb-4">{t("confirm_email_error_help")}</p>
            <Link to="/login" className="btn-gold w-full justify-center" data-testid="confirm-go-login">
              {t("confirm_email_go_login")}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
